import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { ScrollEndFade } from '@/components';
import { useInfiniteScroll } from '@/hooks';
import { SourceFolderItem } from './SourceFolderItem';
import { SourceFolderId } from '../hooks';

export interface SidebarFolderView {
    id: string;
    name: string;
    totalCount: number;
    selectedCount: number;
    isLoading: boolean;
    isSharedByOwner?: boolean;
}

export interface SidebarGroupView {
    id: string;
    title: string;
    scopeId: string;
    totalCount: number;
    selectedCount: number;
    isLoading: boolean;
    folders: SidebarFolderView[];
}

interface SourcesMenuSidebarProps {
    groups: SidebarGroupView[];
    activeFolderId: SourceFolderId;
    onFolderClick: (folderId: SourceFolderId) => void;
    onToggleSelection: (folderId: string) => void;
    maxAdditionalSources?: number | null;
    onLoadMoreFolders?: () => void;
    hasMoreFolders?: boolean;
    isLoadingMoreFolders?: boolean;
    typeFilterSlot?: ReactNode;
}

function selectAllExceedsCapacity(
    totalCount: number,
    selectedCount: number,
    maxAdditionalSources: number | null | undefined,
): boolean {
    if (maxAdditionalSources == null) return false;
    if (totalCount === 0 || selectedCount >= totalCount) return false;
    return totalCount - selectedCount > maxAdditionalSources;
}

function selectionChecked(
    selectedCount: number,
    totalCount: number,
): boolean | 'indeterminate' {
    if (totalCount > 0 && selectedCount >= totalCount) return true;
    if (selectedCount > 0) return 'indeterminate';
    return false;
}

export function SourcesMenuSidebar({
    groups,
    activeFolderId,
    onFolderClick,
    onToggleSelection,
    maxAdditionalSources = null,
    onLoadMoreFolders,
    hasMoreFolders = false,
    isLoadingMoreFolders = false,
    typeFilterSlot,
}: SourcesMenuSidebarProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const canLoadMoreFolders = Boolean(onLoadMoreFolders);

    useInfiniteScroll(scrollContainer, {
        onLoadMore: onLoadMoreFolders ?? (() => {}),
        isLoading: isLoadingMoreFolders,
        hasMore: canLoadMoreFolders && hasMoreFolders,
        threshold: 100,
        enabled: canLoadMoreFolders,
    });

    return (
        <div className="flex min-h-0 w-full max-h-44 shrink-0 flex-col gap-3 @medium:w-[196px] @medium:max-h-none @medium:gap-6">
            {typeFilterSlot && <div className="shrink-0">{typeFilterSlot}</div>}
            <div className="relative min-h-0 flex-1">
                <div
                    ref={setScrollContainer}
                    className="h-full space-y-4 overflow-y-auto scrollbar-hide"
                >
                    {groups.map((group) => (
                        <div key={group.id} className="space-y-1">
                            <SourceFolderItem
                                id={group.scopeId}
                                name={group.title}
                                totalCount={group.totalCount}
                                selectedCount={group.selectedCount}
                                isActive={activeFolderId === group.scopeId}
                                onClick={() => onFolderClick(group.scopeId)}
                                onSelectFolder={onToggleSelection}
                                checked={selectionChecked(
                                    group.selectedCount,
                                    group.totalCount,
                                )}
                                selectDisabled={selectAllExceedsCapacity(
                                    group.totalCount,
                                    group.selectedCount,
                                    maxAdditionalSources,
                                )}
                                isLoading={group.isLoading}
                            />
                            {group.folders.length > 0 && (
                                <div className="space-y-1 pl-2">
                                    {group.folders.map((folder) => (
                                        <SourceFolderItem
                                            key={folder.id}
                                            id={folder.id}
                                            name={folder.name}
                                            totalCount={folder.totalCount}
                                            selectedCount={folder.selectedCount}
                                            isActive={
                                                activeFolderId === folder.id
                                            }
                                            onClick={() =>
                                                onFolderClick(folder.id)
                                            }
                                            onSelectFolder={onToggleSelection}
                                            checked={selectionChecked(
                                                folder.selectedCount,
                                                folder.totalCount,
                                            )}
                                            selectDisabled={selectAllExceedsCapacity(
                                                folder.totalCount,
                                                folder.selectedCount,
                                                maxAdditionalSources,
                                            )}
                                            isLoading={folder.isLoading}
                                            isSharedByOwner={
                                                folder.isSharedByOwner
                                            }
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                    {isLoadingMoreFolders && (
                        <div className="py-2 text-center text-xs text-grey-300">
                            {t('loadingMore')}
                        </div>
                    )}
                </div>
                <ScrollEndFade
                    container={scrollContainer}
                    contentVersions={[groups, isLoadingMoreFolders]}
                />
            </div>
        </div>
    );
}
